// Hook personalizado para la lógica del AdminDashboard
// Extrae estados, efectos y handlers del componente principal

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  subscribeToBids,
  unsubscribe,
  updateProperty,
  createProperty,
  getArchivedProperties,
  unarchiveProperty,
  deleteProperty as deletePropertyService,
  getProperties,
  startBlindBiddingPhase,
  closeBlindBiddingPhase,
  startOpenBiddingPhase
} from '../services/supabaseService';
import { activateBlindBiddingPhase } from '../services/supabase/blindOffers';
import { usePropertyData } from './usePropertyData';

/**
 * Hook que maneja toda la lógica del AdminDashboard
 * - Estados de UI (modales, modo archivadas)
 * - Carga de datos archivados
 * - Handlers para CRUD de propiedades
 * - Suscripciones en tiempo real
 */
export const useAdminDashboard = (
  properties,
  bids,
  invites,
  onSaveProperties,
  onSaveBids,
  onSaveInvites,
  loading,
  onRefresh,
  showNotification // ✅ NUEVO: Función para mostrar notificaciones
) => {
  const navigate = useNavigate();
  
  // ============================================
  // ESTADOS DE UI
  // ============================================
  const [showCreateProperty, setShowCreateProperty] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedBidForConditions, setSelectedBidForConditions] = useState(null);
  const [propertyToClose, setPropertyToClose] = useState(null);
  const [propertyToArchive, setPropertyToArchive] = useState(null);
  const [propertyToStartLastChance, setPropertyToStartLastChance] = useState(null);
  // Estados de fase ciega
  const [propertyToStartOpen, setPropertyToStartOpen] = useState(null);
  const [blindOffersForOpen, setBlindOffersForOpen] = useState([]);
  const [propertyToStartBlind, setPropertyToStartBlind] = useState(null);
  const [propertyToActivateBlindActive, setPropertyToActivateBlindActive] = useState(null);
  const [blindOffersForActivation, setBlindOffersForActivation] = useState([]);
  const [propertyToCloseBlind, setPropertyToCloseBlind] = useState(null);

  // Estado de modo archivadas (leer de sessionStorage si existe)
  const [showArchived, setShowArchived] = useState(() => {
    const stored = sessionStorage.getItem('adminShowArchived');
    return stored === 'true';
  });

  const [archivedProperties, setArchivedProperties] = useState([]);
  const [loadingArchived, setLoadingArchived] = useState(false);

  // ============================================
  // HOOK DE DATOS DE PROPIEDADES
  // ============================================
  const {
    selectedProperty,
    setSelectedProperty,
    getPropertyBids,
    getPropertyInvites,
    deleteProperty,
    getStats,
    refreshPropertyBids
  } = usePropertyData(
    properties, 
    bids, 
    invites, 
    onSaveProperties, 
    onSaveBids, 
    onSaveInvites
  );

  const stats = getStats();

  // ============================================
  // EFECTOS
  // ============================================

  // Cargar bids con condiciones al montar
  useEffect(() => {
    const loadBidsWithConditions = async () => {
      for (const property of properties) {
        const propertyBids = bids.filter(b => b.propertyId === property.id);
        if (propertyBids.length > 0) {
          await refreshPropertyBids(property.id);
        }
      }
    };

    if (!loading && properties.length > 0) {
      loadBidsWithConditions();
    }
  }, [properties, bids, loading, refreshPropertyBids]);

  // Suscribirse a cambios en bids en tiempo real
  useEffect(() => {
    if (properties.length === 0) return;

    const propertyIds = properties.map(p => p.id);
    
    const subscription = subscribeToBids(propertyIds, async (payload) => {
      console.log('📩 Cambio en bids detectado:', payload);

      if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
        const propertyId = payload.new.property_id;
        await refreshPropertyBids(propertyId);
      }
    });

    return () => {
      unsubscribe(subscription);
    };
  }, [properties, refreshPropertyBids]);

  // Cargar archivadas cuando se activa el toggle
  useEffect(() => {
    const loadArchivedProperties = async () => {
      if (!showArchived) return;
      
      setLoadingArchived(true);
      try {
        const data = await getArchivedProperties();
        setArchivedProperties(data);
        console.log('📦 Propiedades archivadas cargadas:', data.length);
      } catch (error) {
        console.error('Error cargando propiedades archivadas:', error);
      } finally {
        setLoadingArchived(false);
      }
    };

    loadArchivedProperties();
  }, [showArchived]);

  // ============================================
  // HANDLERS
  // ============================================

  // Abrir modal de archivar
  const handleArchiveProperty = useCallback((property) => {
    setPropertyToArchive(property);
  }, []);

  // Confirmar archivado
  const confirmArchive = useCallback(async () => {
    if (!propertyToArchive) return;

    try {
      setSaving(true);     
      await deletePropertyService(propertyToArchive.id);
      
      // ✅ FIX: Usar onRefresh en lugar de onSaveProperties
      if (onRefresh) {
        await onRefresh();
      }
      
      setArchivedProperties([]);
      setPropertyToArchive(null);
    } catch (error) {
      console.error('Error archivando propiedad:', error);
      if (showNotification) {
        showNotification('error', 'Error', 'Error al archivar la propiedad. Intenta de nuevo.');
      }
    } finally {
      setSaving(false);
    }
  }, [propertyToArchive, onRefresh, showNotification]);

  // Reactivar propiedad archivada
  // ✅ FIX: Usar onRefresh en lugar de onSaveProperties para evitar duplicados
  const handleUnarchiveProperty = useCallback(async (propertyId) => {
    try {
      setSaving(true);
      
      // 1. Limpiar sessionStorage ANTES de todo (antes de cualquier refresh que remonte)
      sessionStorage.removeItem('adminShowArchived');
      
      // 2. Hacer UPDATE en Supabase (is_active = true)
      await unarchiveProperty(propertyId);
      
      // 3. Recargar datos desde Supabase
      if (onRefresh) {
        await onRefresh();
      }
      
      // 4. Actualizar lista de archivadas
      const updatedArchived = await getArchivedProperties();
      setArchivedProperties(updatedArchived);
      
      // 5. Cambiar a vista de activas
      setShowArchived(false);

    } catch (error) {
      console.error('Error reactivando propiedad:', error);
      if (showNotification) {
        showNotification('error', 'Error', 'Error al reactivar la propiedad. Intenta de nuevo.');
      }
    } finally {
      setSaving(false);
    }
  }, [onRefresh, showNotification]);

  // Iniciar última oportunidad manualmente
  const handleStartLastChance = useCallback(async (property, startDateTime, deadline) => {
    try {
      setSaving(true);

      const updatedPropertyData = {
        ...property,
        lastChanceStartedAt: startDateTime.toISOString(),
        last_chance_deadline: deadline.toISOString(), // Guardar deadline exacto
      };

      await updateProperty(property.id, updatedPropertyData);

      if (onRefresh) {
        await onRefresh();
      }

      setPropertyToStartLastChance(null);

    } catch (error) {
      console.error('Error iniciando última oportunidad:', error);
      if (showNotification) {
        showNotification('error', 'Error', 'Error al iniciar la última oportunidad. Por favor, intenta de nuevo.');
      }
    } finally {
      setSaving(false);
    }
  }, [onRefresh, showNotification]);

  // Cerrar última oportunidad
  const handleCloseLastChance = useCallback(async (property) => {
    try {
      setSaving(true);
      
      const updatedPropertyData = {
        ...property,
        lastChanceClosedAt: new Date().toISOString()
      };
      
      await updateProperty(property.id, updatedPropertyData);
      
      // ✅ FIX: Usar onRefresh en lugar de onSaveProperties
      if (onRefresh) {
        await onRefresh();
      }
      
      setPropertyToClose(null);

    } catch (error) {
      console.error('Error cerrando última oportunidad:', error);
      if (showNotification) {
        showNotification('error', 'Error', 'Error al cerrar la última oportunidad. Por favor, intenta de nuevo.');
      }
    } finally {
      setSaving(false);
    }
  }, [onRefresh, showNotification]);

  // ============================================
  // HANDLERS DE FASE CIEGA
  // ============================================

  // Iniciar fase de oferta ciega (UPCOMING → BLIND_WAITING) - Abrir modal
  const handleStartBlindBidding = useCallback((property) => {
    setPropertyToStartBlind(property);
  }, []);

  // Confirmar inicio de fase ciega desde el modal
  const confirmStartBlindBidding = useCallback(async (greenThreshold, yellowThreshold) => {
    if (!propertyToStartBlind) return;

    try {
      setSaving(true);

      // Iniciar la fase de oferta ciega con los umbrales del semáforo
      await startBlindBiddingPhase(propertyToStartBlind.id, greenThreshold, yellowThreshold);

      if (onRefresh) {
        await onRefresh();
      }

      setPropertyToStartBlind(null);
    } catch (error) {
      console.error('Error iniciando fase ciega:', error);
      if (showNotification) {
        showNotification('error', 'Error', 'Error al iniciar la fase de oferta ciega.');
      }
    } finally {
      setSaving(false);
    }
  }, [propertyToStartBlind, onRefresh, showNotification]);

  // Activar manualmente BLIND_ACTIVE (BLIND_WAITING → BLIND_ACTIVE)
  // Usado cuando hay ofertas amarillas/rojas que requieren aprobación manual
  const handleActivateBlindActive = useCallback((property, blindOffers) => {
    setPropertyToActivateBlindActive(property);
    setBlindOffersForActivation(blindOffers);
  }, []);

  // Confirmar activación manual de BLIND_ACTIVE
  const confirmActivateBlindActive = useCallback(async (deadline) => {
    if (!propertyToActivateBlindActive) return;

    try {
      setSaving(true);
      await activateBlindBiddingPhase(
        propertyToActivateBlindActive.id,
        deadline
      );

      if (onRefresh) {
        await onRefresh();
      }

      setPropertyToActivateBlindActive(null);
      setBlindOffersForActivation([]);

      if (showNotification) {
        showNotification('success', 'Fase activa iniciada', 'El período ha comenzado. Se notificará a los invitados habilitados.');
      }
    } catch (error) {
      console.error('Error activando fase BLIND_ACTIVE:', error);
      if (showNotification) {
        showNotification('error', 'Error', 'Error al activar la fase activa de oferta ciega.');
      }
    } finally {
      setSaving(false);
    }
  }, [propertyToActivateBlindActive, onRefresh, showNotification]);

  // Cerrar directamente desde el modal de activación (saltar BLIND_ACTIVE → CLOSED)
  const skipBlindActiveToClose = useCallback(async () => {
    if (!propertyToActivateBlindActive) return;

    try {
      setSaving(true);
      await closeBlindBiddingPhase(propertyToActivateBlindActive.id);

      if (onRefresh) {
        await onRefresh();
      }

      setPropertyToActivateBlindActive(null);
      setBlindOffersForActivation([]);

      if (showNotification) {
        showNotification('success', 'Fase ciega cerrada', 'Puedes abrir la subasta abierta cuando estés listo.');
      }
    } catch (error) {
      console.error('Error cerrando fase ciega:', error);
      if (showNotification) {
        showNotification('error', 'Error', 'Error al cerrar la fase de oferta ciega.');
      }
    } finally {
      setSaving(false);
    }
  }, [propertyToActivateBlindActive, onRefresh, showNotification]);

  // Abrir modal de confirmación para cerrar fase ciega
  const handleCloseBlindBidding = useCallback((property) => {
    setPropertyToCloseBlind(property);
  }, []);

  // Confirmar cierre de fase ciega desde el modal
  const confirmCloseBlindBidding = useCallback(async () => {
    if (!propertyToCloseBlind) return;

    try {
      setSaving(true);
      await closeBlindBiddingPhase(propertyToCloseBlind.id);

      if (onRefresh) {
        await onRefresh();
      }

      setPropertyToCloseBlind(null);

      if (showNotification) {
        showNotification('success', 'Fase ciega cerrada', 'Puedes abrir la subasta abierta cuando estés listo.');
      }
    } catch (error) {
      console.error('Error cerrando fase ciega:', error);
      if (showNotification) {
        showNotification('error', 'Error', 'Error al cerrar la fase de oferta ciega.');
      }
    } finally {
      setSaving(false);
    }
  }, [propertyToCloseBlind, onRefresh, showNotification]);

  // ✅ ACTUALIZADO: Abrir modal combinado para iniciar fase OPEN con selección de finalistas
  const handleStartOpenBidding = useCallback((property, blindOffers) => {
    setPropertyToStartOpen(property);
    setBlindOffersForOpen(blindOffers);
  }, []);

  // ✅ ACTUALIZADO: Confirmar inicio de fase OPEN con finalistas y configuración de fechas
  const confirmStartOpenBidding = useCallback(async (config) => {
    if (!propertyToStartOpen) return;

    console.log('🔍 Hook confirmStartOpenBidding - Received config:', config);
    console.log('🔍 propertyToStartOpen:', propertyToStartOpen);

    const { startingPrice, biddingStartDate, deadline, minIncrement, maxBidPercentage, finalistInviteIds, selectedOffers } = config;

    console.log('🔍 Extracted values:', {
      startingPrice,
      biddingStartDate,
      deadline,
      minIncrement,
      maxBidPercentage,
      finalistInviteIds,
      selectedOffersCount: selectedOffers?.length || 0
    });

    try {
      setSaving(true);
      console.log('🔍 Calling startOpenBiddingPhase with:', {
        propertyId: propertyToStartOpen.id,
        startingPrice,
        biddingStartDate,
        deadline,
        minIncrement,
        maxBidPercentage,
        finalistInviteIds,
        selectedOffersCount: selectedOffers?.length || 0
      });

      await startOpenBiddingPhase(
        propertyToStartOpen.id,
        startingPrice,
        biddingStartDate,
        deadline,
        minIncrement,
        maxBidPercentage,
        finalistInviteIds,
        selectedOffers || []
      );

      if (onRefresh) {
        await onRefresh();
      }

      setPropertyToStartOpen(null);
      setBlindOffersForOpen([]);

      if (showNotification) {
        const startDate = new Date(biddingStartDate);
        const scheduleMsg = ` (inicio: ${startDate.toLocaleString()})`;
        showNotification('success', 'Subasta abierta configurada', `${finalistInviteIds.length} finalistas seleccionados. Precio inicial: ${startingPrice.toLocaleString()}€${scheduleMsg}`);
      }
    } catch (error) {
      console.error('❌ Error iniciando fase OPEN:', error);
      console.error('❌ Error details:', error.message, error.stack);
      if (showNotification) {
        showNotification('error', 'Error', 'Error al iniciar la subasta abierta.');
      }
    } finally {
      setSaving(false);
    }
  }, [propertyToStartOpen, onRefresh, showNotification]);

  // Guardar propiedad (crear o actualizar)
  const handleSaveProperty = useCallback(async (propertyData) => {
    setSaving(true);
    try {
      const isExistingProperty = propertyData.id && properties.some(p => p.id === propertyData.id);
      
      if (isExistingProperty) {
        await updateProperty(propertyData.id, propertyData);
      } else {
        await createProperty(propertyData);
      }

      // ✅ FIX: Usar onRefresh en lugar de onSaveProperties
      if (onRefresh) {
        await onRefresh();
      }
      
      setShowCreateProperty(false);
      setEditingProperty(null);
    } catch (error) {
      console.error('Error guardando propiedad:', error);
      if (showNotification) {
        showNotification('error', 'Error', 'Error al guardar la propiedad. Intenta de nuevo.');
      }
    } finally {
      setSaving(false);
    }
  }, [properties, onRefresh, showNotification]);

  // Guardar invitaciones
  const handleSaveInvites = useCallback(async (newInvites) => {
    setSaving(true);
    try {
      await onSaveInvites(newInvites);
    } catch (error) {
      console.error('Error guardando invitaciones:', error);
      if (showNotification) {
        showNotification('error', 'Error', 'Error al guardar las invitaciones. Intenta de nuevo.');
      }
    } finally {
      setSaving(false);
    }
  }, [onSaveInvites, showNotification]);

  // Ver condiciones de una oferta
  const handleViewConditions = useCallback((bid) => {
    setSelectedBidForConditions(bid);
  }, []);

  // Toggle modo archivadas
  const toggleArchived = useCallback(() => {
    setShowArchived(prev => !prev);
  }, []);

  // ============================================
  // VALORES DERIVADOS
  // ============================================
  const displayedProperties = showArchived ? archivedProperties : properties;
  const isLoadingDisplay = showArchived ? loadingArchived : loading;

  // ============================================
  // RETURN
  // ============================================
  return {
    // Estados de UI
    showCreateProperty,
    setShowCreateProperty,
    editingProperty,
    setEditingProperty,
    saving,
    selectedBidForConditions,
    setSelectedBidForConditions,
    propertyToClose,
    setPropertyToClose,
    propertyToArchive,
    setPropertyToArchive,
    propertyToStartLastChance,
    setPropertyToStartLastChance,
    // Estados de fase ciega
    propertyToStartOpen,
    setPropertyToStartOpen,
    blindOffersForOpen,
    setBlindOffersForOpen,
    propertyToStartBlind,
    setPropertyToStartBlind,
    propertyToActivateBlindActive,
    setPropertyToActivateBlindActive,
    blindOffersForActivation,
    propertyToCloseBlind,
    setPropertyToCloseBlind,

    // Estado archivadas
    showArchived,
    toggleArchived,
    archivedProperties,
    loadingArchived,
    
    // Datos
    stats,
    displayedProperties,
    isLoadingDisplay,
    selectedProperty,
    setSelectedProperty,
    
    // Funciones de datos
    getPropertyBids,
    getPropertyInvites,
    
    // Handlers
    handleArchiveProperty,
    confirmArchive,
    handleUnarchiveProperty,
    handleStartLastChance,
    handleCloseLastChance,
    handleStartBlindBidding,
    confirmStartBlindBidding,
    handleActivateBlindActive,
    confirmActivateBlindActive,
    skipBlindActiveToClose,
    handleCloseBlindBidding,
    confirmCloseBlindBidding,
    handleStartOpenBidding,
    confirmStartOpenBidding,
    handleSaveProperty,
    handleSaveInvites,
    handleViewConditions,
  };
};